import * as React from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { QueueListIcon } from '@heroicons/react/16/solid'
import { Button, ButtonText } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import AccountMenu from '@/layouts/AccountMenu'
import SpacesMenu from '@/layouts/SpacesMenu'
import HorizonMenu from '@/layouts/HorizonMenu'
import {
  FeatureGate,
  useFeaturesLoaded,
  useHasFeature,
} from '@/components/feature-gate'
import useGetTodoCount from '@/services/api/to-do/useGetTodoCount'
import AddTransactionMenu from '@/mobile/AddTransactionMenu'
import type { DeviceClass } from '@/mobile/useDeviceClass'

function ViewSwitch({
  dashboard,
  large,
}: {
  dashboard: boolean
  large: boolean
}) {
  const navigate = useNavigate()
  const item = (active: boolean) =>
    cn(
      'flex items-center justify-center rounded-lg transition-colors',
      large ? 'h-[38px] flex-1 text-[13.5px]' : 'px-3 py-[5px] text-[12.5px]',
      active
        ? 'bg-[hsl(222,47%,11%)] font-semibold text-white'
        : 'font-medium text-txt-secondary',
    )
  return (
    <div
      role="tablist"
      className={cn(
        'flex rounded-[9px] border border-light bg-layout-secondary p-[3px]',
        large && 'w-full',
      )}
    >
      <button
        type="button"
        role="tab"
        aria-selected={dashboard}
        className={item(dashboard)}
        onClick={() => navigate('/')}
      >
        Dashboard
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={!dashboard}
        className={item(!dashboard)}
        onClick={() => navigate('/table')}
      >
        Table
      </button>
    </div>
  )
}

function TodosButton({ withLabel }: { withLabel: boolean }) {
  const hasTodoFeature = useHasFeature('to-do')
  const featuresLoaded = useFeaturesLoaded()
  const { data: todoCount } = useGetTodoCount({
    enabled: featuresLoaded && hasTodoFeature,
  })

  return (
    <FeatureGate feature="to-do" mode="wrap">
      <Link to="/my-todos" aria-label="My Todos">
        {withLabel ? (
          <Button variant="outline" className="rounded-[9px]">
            <QueueListIcon />
            <ButtonText>To-dos</ButtonText>
            <span className="rounded-full bg-accent-tr-24 px-1.5 text-[11px] font-bold text-txt-accent">
              {todoCount ?? 0}
            </span>
          </Button>
        ) : (
          <Button
            variant="outline"
            iconOnly
            size="lg"
            className="relative size-10 rounded-[9px]"
          >
            <QueueListIcon />
            <span className="absolute -right-1 -top-1 min-w-5 rounded-full bg-accent-default px-[5px] text-[10px] font-bold leading-4 text-white">
              {todoCount ?? 0}
            </span>
          </Button>
        )}
      </Link>
    </FeatureGate>
  )
}

/**
 * Top bar for phone and tablet. Money Paths and Models have no entry here —
 * the mobile design leaves them out for now.
 */
export default function MobileTopBar({
  device,
  dashboard,
}: {
  device: Exclude<DeviceClass, 'desktop'>
  dashboard: boolean
}) {
  if (device === 'tablet') {
    return (
      <div className="flex h-[52px] shrink-0 items-center justify-between gap-3 border-b border-primary bg-white px-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <AccountMenu />
          <SpacesMenu />
          <ViewSwitch dashboard={dashboard} large={false} />
        </div>
        <div className="flex items-center gap-2">
          <TodosButton withLabel />
          {dashboard && <HorizonMenu />}
          <AddTransactionMenu device="tablet" />
        </div>
      </div>
    )
  }

  return (
    <div className="flex shrink-0 flex-col gap-[9px] border-b border-primary bg-white px-3 pb-2.5 pt-[max(6px,env(safe-area-inset-top))]">
      <div className="flex items-center justify-between gap-2.5">
        <div className="flex min-w-0 items-center gap-2">
          <AccountMenu />
          <div className="min-w-0 [&_button]:max-w-full">
            <SpacesMenu />
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <TodosButton withLabel={false} />
          <AddTransactionMenu device="phone" />
        </div>
      </div>
      <ViewSwitch dashboard={dashboard} large />
    </div>
  )
}
